import { categoryLabel } from "@/features/cost-allocations/utils/categories";
import { formatMoney } from "@/features/cost-allocations/utils/formatMoney";
import { formatPeriod } from "@/features/cost-allocations/utils/formatPeriod";
import { LIFECYCLE_LABEL } from "../lifecycleStates";
import type { AnimalEventType } from "../types";

type Payload = Record<string, unknown> | null;

function asString(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function asNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function lifecycleLabel(value: unknown): string | null {
  const s = asString(value);
  if (!s) return null;
  return (LIFECYCLE_LABEL as Record<string, string>)[s] ?? s;
}

function shortId(id: string): string {
  return id.length > 8 ? `${id.slice(0, 8)}…` : id;
}

// B82: la apreciación puede ser negativa (pérdida de valor). formatMoney
// devuelve "—" para negativos (los costos nunca lo son), así que formateamos el
// valor absoluto y anteponemos el signo. "+₡9.605" comunica valor ganado;
// "-₡50" una pérdida del período.
function formatSignedMoney(value: unknown): string | null {
  const num =
    typeof value === "string" ? Number(value) : typeof value === "number" ? value : Number.NaN;
  if (!Number.isFinite(num)) return null;
  const sign = num < 0 ? "-" : "+";
  return `${sign}${formatMoney(Math.abs(num))}`;
}

function genericFallback(payload: Payload): string {
  if (!payload || Object.keys(payload).length === 0) return "Sin detalles";
  try {
    return JSON.stringify(payload);
  } catch {
    return "Sin detalles";
  }
}

const FORMATTERS: Partial<Record<AnimalEventType, (p: Payload) => string>> = {
  animal_registered: (p) => {
    const source = asString(p?.source);
    if (source === "born") return "Nacido en finca";
    if (source === "imported") return "Importado al sistema";
    return "Registro inicial";
  },

  animal_purchased: (p) => {
    const cost = p?.cost;
    const seller = asString(p?.seller);
    const parts: string[] = [];
    if (cost !== undefined && cost !== null) parts.push(formatMoney(cost as string | number));
    if (seller) parts.push(`a ${seller}`);
    return parts.length > 0 ? parts.join(" · ") : "Compra registrada";
  },

  lot_cost_allocated: (p) => {
    const category = asString(p?.category);
    const start = asString(p?.period_start);
    const end = asString(p?.period_end);
    const amount = p?.amount;

    const parts: string[] = [];
    if (category) parts.push(categoryLabel(category));
    if (amount !== undefined && amount !== null) {
      parts.push(formatMoney(amount as string | number));
    }
    if (start && end) parts.push(formatPeriod(start, end));

    return parts.length > 0 ? parts.join(" · ") : "Costo asignado";
  },

  stage_transitioned: (p) => {
    const from = lifecycleLabel(p?.from);
    const to = lifecycleLabel(p?.to);
    if (from && to) return `De ${from} a ${to}`;
    if (to) return `A ${to}`;
    return "Cambio de etapa";
  },

  lot_changed: (p) => {
    const fromId = asString(p?.fromLotId);
    const toId = asString(p?.toLotId);
    if (fromId && toId) return `De lote ${shortId(fromId)} a lote ${shortId(toId)}`;
    if (toId) return `A lote ${shortId(toId)}`;
    return "Cambio de lote";
  },

  first_calving_recorded: (p) => {
    const calfBorn = p?.calfBorn;
    const months = asNumber(p?.usefulLifeMonths);
    const parts: string[] = ["Primer parto"];
    if (calfBorn === true) parts.push("ternero vivo");
    if (calfBorn === false) parts.push("sin ternero vivo");
    if (months !== null) parts.push(`vida útil ${months} meses`);
    return parts.join(" · ");
  },

  calving_recorded: (p) => {
    const n = asNumber(p?.lactationNumber);
    return n !== null ? `Lactancia número ${n}` : "Parto registrado";
  },

  dry_off_recorded: () => "Vaca puesta en seco",

  depreciation_accrued: (p) => {
    const month = asString(p?.month);
    const amount = p?.amount;
    const parts: string[] = [];
    if (amount !== undefined && amount !== null) {
      parts.push(formatMoney(amount as string | number));
    }
    if (month) parts.push(month);
    return parts.length > 0 ? parts.join(" · ") : "Depreciación";
  },

  appreciation_accrued: (p) => {
    const period = asString(p?.period);
    const amount = formatSignedMoney(p?.appreciation);
    const parts: string[] = [];
    if (amount) parts.push(amount);
    if (period) parts.push(period);
    return parts.length > 0 ? parts.join(" · ") : "Apreciación";
  },

  animal_died: (p) => {
    const cause = asString(p?.cause);
    return cause ? `Causa: ${cause}` : "Muerte registrada";
  },

  animal_culled: (p) => {
    const reason = asString(p?.reason);
    return reason ? `Motivo: ${reason}` : "Descarte registrado";
  },

  animal_sold: (p) => {
    const price = p?.price;
    const buyer = asString(p?.buyer);
    const parts: string[] = [];
    if (price !== undefined && price !== null) parts.push(formatMoney(price as string | number));
    if (buyer) parts.push(`a ${buyer}`);
    return parts.length > 0 ? parts.join(" · ") : "Venta registrada";
  },

  event_corrected: (p) => {
    const originalType = asString(p?.originalEventType);
    const reason = asString(p?.correctionReason);
    const parts: string[] = ["Corrección"];
    if (originalType) parts.push(`del evento ${originalType}`);
    if (reason) parts.push(`motivo: ${reason}`);
    return parts.join(" · ");
  },
};

export function formatEventPayload(eventType: string, payload: Payload): string {
  const formatter = FORMATTERS[eventType as AnimalEventType];
  if (formatter) return formatter(payload);
  return genericFallback(payload);
}
